/**
 * Ziyaretçi analitiği istemcisi — /api/track uç noktasına olay gönderir.
 *
 * NEDEN: Owner 2026-08-01 — hangi ülkeden kaç ziyaretçi geliyor, hangi
 * sayfalarda dolaşıyor bilinmiyordu. Ülke dağılımı hangi dile öncelik
 * verileceğini belirliyor (Gana/Fransa trafiği bu ihtiyacı doğurdu).
 *
 * Gizlilik: ziyaretçi kimliği rastgele üretilir ve `sessionStorage`'da tutulur —
 * sekme kapanınca silinir, kalıcı çerez yok, kişiye geri bağlanamaz. Ülke bilgisi
 * sunucuda Vercel başlığından okunur; IP istemciden hiç gönderilmez.
 */
import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import i18n from '../i18n'

const SESSION_KEY = 'hnw_sid'
const ENDPOINT = '/api/track'

/** Yerel geliştirmede API yok — olaylar gönderilmez. */
const isLocalDev = typeof window !== 'undefined'
  && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname)

function sessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY)
    if (!id) {
      id = (crypto.randomUUID?.() ?? String(Math.random()).slice(2) + Date.now().toString(36))
      sessionStorage.setItem(SESSION_KEY, id)
    }
    return id
  } catch {
    return 'no-storage' // gizli sekme / storage kapalı
  }
}

interface EventPayload {
  event: 'pageview' | 'click'
  path: string
  lang: string
  referrer?: string
  label?: string
}

function send(payload: Omit<EventPayload, 'lang'>): void {
  if (isLocalDev) return
  const body = JSON.stringify({
    ...payload,
    lang: i18n.language?.slice(0, 2) || 'tr',
    sessionId: sessionId(),
  })
  try {
    // sendBeacon sayfa kapanırken bile teslim eder ve isteği bloklamaz
    if (navigator.sendBeacon?.(ENDPOINT, new Blob([body], { type: 'application/json' }))) return
  } catch { /* sendBeacon yoksa fetch'e düş */ }
  fetch(ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
  }).catch(() => { /* analitik kaybı sessizdir */ })
}

/** Elle olay göndermek için (form gönderimi, CTA tıklaması vb.). */
export function trackEvent(label: string): void {
  send({ event: 'click', path: window.location.pathname, label })
}

/**
 * Uygulama kökünde bir kez çağrılır. Rota değiştikçe pageview,
 * `data-track` taşıyan öğelere tıklandıkça click olayı yollar.
 */
export function useAnalytics(): void {
  const { pathname } = useLocation()
  const lastPath = useRef<string | null>(null)

  useEffect(() => {
    if (lastPath.current === pathname) return
    const isFirst = lastPath.current === null
    lastPath.current = pathname
    send({
      event: 'pageview',
      path: pathname,
      // yalnızca siteye ilk girişte dış kaynak anlamlı; iç geçişlerde gürültü
      referrer: isFirst ? document.referrer || undefined : undefined,
    })
  }, [pathname])

  useEffect(() => {
    const onClick = (e: MouseEvent): void => {
      const el = (e.target as HTMLElement | null)?.closest?.('[data-track]')
      if (!el) return
      const label = el.getAttribute('data-track')
      if (label) send({ event: 'click', path: window.location.pathname, label })
    }
    document.addEventListener('click', onClick, { capture: true })
    return () => document.removeEventListener('click', onClick, { capture: true })
  }, [])
}
